/**
 * Kiosk API Service
 * 
 * This service provides methods to interact with the secure backend API
 * instead of making direct calls to the WooCommerce API.
 */

import { get, post, put, del } from '../utils/api';
import settings from '../config/settings';
import { sanitizeEmail } from '../utils/customerEmail';

/**
 * Product-related API calls
 */
export const products = {
  /**
   * Search for products
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} List of products
   */
  search: async (searchTerm = '', page = 1, perPage = 25) => {
    try {
      const params = {
        ...(searchTerm && { search: searchTerm }),
        page,
        per_page: perPage
      };

      const response = await get('/api/products', params);

      // Expected shape: { data: [...], meta: {...} }
      const data = Array.isArray(response?.data) ? response.data : [];
      const meta = response?.meta || {};

      // Debug log for product data
      if (data.length > 0) {
        console.log('Products API response sample:', {
          productCount: data.length,
          firstProduct: {
            id: data[0].id,
            name: data[0].name,
            hasImages: !!data[0].images,
            imageCount: data[0].images?.length || 0,
            firstImage: data[0].images?.[0],
            imageSrc: data[0].images?.[0]?.src,
            imageSrcType: typeof data[0].images?.[0]?.src
          }
        });
      }

      return { data, meta };
    } catch (error) {
      console.error('Error searching products:', error);
      // Return empty result instead of throwing to prevent UI crashes
      return { data: [], meta: { page, per_page: perPage, total_items: 0, total_pages: 0 } };
    }
  },

  /**
   * Get product by ID
   * @param {number|string} productId - Product ID
   * @returns {Promise<Object>} Product details
   */
  getById: async (productId) => {
    if (!productId) {
      throw new Error('Product ID is required');
    }
    return await get(`/kiosk-products/${productId}`);
  },

  /**
   * Get product variations
   * @param {number|string} productId - Product ID
   * @returns {Promise<Array>} List of product variations
   */
  getVariations: async (productId) => {
    if (!productId) {
      throw new Error('Product ID is required');
    }
    return await get(`/kiosk-products/${productId}/variations`);
  },

  // Local DB endpoints (/ldb)
  searchLocal: async (searchTerm = '', page = 1, perPage = 25) => {
    try {
      const params = {
        ...(searchTerm && { q: searchTerm }),
        page,
        per_page: perPage
      };
      const res = await get('/ldb/products', params);
      const data = Array.isArray(res?.data) ? res.data : [];
      const meta = res?.meta || { page, per_page: perPage };
      return { data, meta };
    } catch (e) {
      console.error('Error searching local products:', e);
      return { data: [], meta: { page, per_page: perPage, total: 0 } };
    }
  },
  getLocalById: async (productId) => {
    if (!productId) throw new Error('Product ID is required');
    return await get(`/ldb/products/${productId}`);
  }
};

/**
 * Customer-related API calls
 */
export const customers = {
  normalizeLocalCustomer: (customer = {}) => ({
    ...customer,
    id: customer.id,
    customer_id: customer.customer_id || customer.id,
    user_id: customer.user_id || customer.id,
    email: customer.email || customer.billing_email || '',
    first_name: customer.first_name || customer.billing_first_name || '',
    last_name: customer.last_name || customer.billing_last_name || '',
    phone: customer.phone || customer.billing_phone || '',
    billing: {
      first_name: customer.billing?.first_name || customer.billing_first_name || customer.first_name || '',
      last_name: customer.billing?.last_name || customer.billing_last_name || customer.last_name || '',
      email: customer.billing?.email || customer.billing_email || customer.email || '',
      phone: customer.billing?.phone || customer.billing_phone || customer.phone || '',
      city: customer.billing?.city || customer.billing_city || '',
      state: customer.billing?.state || customer.billing_state || ''
    }
  }),

  /**
   * Search for customers
   * @param {string} searchTerm - Search term (email, name, etc.)
   * @returns {Promise<Array>} List of customers
   */
  search: async (searchTerm = '') => {
    try {
      const response = await get('/ldb/customers', { q: searchTerm, page: 1, per_page: 25 });
      return Array.isArray(response?.data) ? response.data : [];
    } catch (error) {
      console.error('Error searching customers:', error);
      throw error;
    }
  },
  
  /**
   * Search for users via API
   * @param {string} searchTerm - Search term (email, name, phone, etc.)
   * @returns {Promise<Array>} List of matching users
   */
  searchUsers: async (searchTerm = '') => {
    try {
      const response = await get('/ldb/customers', { q: searchTerm, page: 1, per_page: 25 });
      const data = Array.isArray(response?.data) ? response.data : [];
      return data.map((customer) => customers.normalizeLocalCustomer(customer));
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  },

  /**
   * Create a new customer
   * @param {Object} customerData - Customer data
   * @returns {Promise<Object>} Created customer
   */
  create: async (customerData) => {
    if (!customerData || typeof customerData !== 'object') {
      const error = new Error('Customer data is required');
      console.error('Customer creation error:', error);
      throw error;
    }
    
    // Create a clean copy of the customer data to avoid circular references
    const cleanCustomerData = {
      email: sanitizeEmail(customerData.email),
      first_name: customerData.first_name,
      last_name: customerData.last_name,
      phone: customerData.phone,
      address1: customerData.address1,
      address2: customerData.address2,
      city: customerData.city,
      state: customerData.state,
      zipCode: customerData.zipCode,
      country: customerData.country
    };
    
    console.log('Creating customer with data:', cleanCustomerData);
    
    try {
      const result = await post('/kiosk-customers', cleanCustomerData);
      console.log('Customer created successfully:', result);
      return result;
    } catch (error) {
      console.error('Error in customer creation:', error);
      throw error;
    }
  },

  /**
   * Get customer by ID
   * @param {number|string} customerId - Customer ID
   * @returns {Promise<Object>} Customer details
   */
  getById: async (customerId) => {
    if (!customerId) {
      throw new Error('Customer ID is required');
    }
    return await get(`/kiosk-customers/${customerId}`);
  },

  /**
   * Update a customer
   * @param {number|string} customerId - Customer ID
   * @param {Object} updates - Updated customer data
   * @returns {Promise<Object>} Updated customer
   */
  update: async (customerId, updates) => {
    if (!customerId) {
      throw new Error('Customer ID is required');
    }
    if (!updates || typeof updates !== 'object') {
      throw new Error('Customer updates are required');
    }
    return await put(`/devotees/${customerId}`, updates);
  },

  // Local DB endpoints (/ldb)
  searchLocal: async (searchTerm = '', page = 1, perPage = 25) => {
    const params = {
      ...(searchTerm && { q: searchTerm }),
      page,
      per_page: perPage
    };
    const res = await get('/ldb/customers', params);
    return Array.isArray(res?.data) ? res.data.map((customer) => customers.normalizeLocalCustomer(customer)) : [];
  },
  getLocalById: async (customerId) => {
    if (!customerId) throw new Error('Customer ID is required');
    const result = await get(`/ldb/customers/${customerId}`);
    return customers.normalizeLocalCustomer(result);
  }
};

/**
 * Order-related API calls
 */
export const orders = {
  /**
   * Create a new order
   * @param {Object} orderData - Order data
   * @returns {Promise<Object>} Created order
   */
  create: async (orderData) => {
    if (!orderData || typeof orderData !== 'object') {
      throw new Error('Order data is required');
    }
    
    // Add payment method details from backend config
    const paymentConfig = settings.payment;
    const orderWithPayment = {
      ...orderData,
      payment_method: paymentConfig.defaultMethod,
      payment_method_title: paymentConfig.defaultMethodTitle,
      set_paid: paymentConfig.setPaid,
      status: paymentConfig.defaultStatus,
      customer_note: paymentConfig.defaultCustomerNote,
      currency: paymentConfig.defaultCurrency
    };
    
    return await post('/kiosk-orders', orderWithPayment);
  },

  /**
   * Get order by ID
   * @param {number|string} orderId - Order ID
   * @returns {Promise<Object>} Order details
   */
  getById: async (orderId) => {
    if (!orderId) {
      throw new Error('Order ID is required');
    }
    return await get(`/kiosk-orders/${orderId}`);
  },

  /**
   * Update order status
   * @param {number|string} orderId - Order ID
   * @param {string} status - New status
   * @returns {Promise<Object>} Updated order
   */
  updateStatus: async (orderId, status) => {
    if (!orderId) {
      throw new Error('Order ID is required');
    }
    if (!status) {
      throw new Error('Status is required');
    }
    return await put(`/kiosk-orders/${orderId}/status`, { status });
  },

  /**
   * Process a payment for an order
   * @param {number|string} orderId - Order ID
   * @param {Object} paymentData - Payment data
   * @returns {Promise<Object>} Payment result
   */
  processPayment: async (orderId, paymentData) => {
    if (!orderId) {
      throw new Error('Order ID is required');
    }
    if (!paymentData || typeof paymentData !== 'object') {
      throw new Error('Payment data is required');
    }
    return await post(`/kiosk-orders/${orderId}/pay`, paymentData);
  }
};

/**
 * Payment-related API calls
 */
export const payments = {
  /**
   * Create a payment intent
   * @param {number} amount - Amount in cents
   * @param {string} currency - Currency code (default: 'usd')
   * @param {Object} metadata - Additional metadata
   * @returns {Promise<Object>} Payment intent
   */
  createIntent: async (amount, currency = 'usd', metadata = {}) => {
    if (!amount || isNaN(amount)) {
      throw new Error('Valid amount is required');
    }
    return await post('/payments/intents', {
      amount,
      currency,
      metadata
    });
  },

  /**
   * Confirm a payment intent
   * @param {string} paymentIntentId - Payment intent ID
   * @param {string} paymentMethodId - Payment method ID
   * @returns {Promise<Object>} Confirmation result
   */
  confirmIntent: async (paymentIntentId, paymentMethodId) => {
    if (!paymentIntentId) {
      throw new Error('Payment intent ID is required');
    }
    return await post(`/payments/intents/${paymentIntentId}/confirm`, {
      payment_method: paymentMethodId
    });
  }
};

// For backward compatibility
export default {
  products,
  customers,
  orders,
  payments,
  // Alias for backward compatibility
  searchProducts: products.search,
  searchCustomers: customers.search,
  createCustomer: customers.create,
  createOrder: orders.create,
  createPaymentIntent: payments.createIntent
};
